/**
 * Transmission-method stored values and their display labels.
 *
 * This is the ONLY place a stored method value becomes visible text. Four sites
 * used to derive the label mechanically with `stored.toUpperCase()`, which worked
 * only by accident: 'api' and 'webhook' happen to uppercase into something
 * readable. The required labels are now mixed case — `'eapi'.toUpperCase()` is
 * `EAPI`, not `eAPI` — so no amount of string manipulation can produce them.
 * Every display site imports from here instead.
 *
 * More providers (SFax, SRFax, eFax PHX) are coming, so the lookups are total by
 * construction: an unmapped value renders legibly via `fallbackMethodLabel`
 * rather than crashing or showing a blank cell.
 *
 * Badge colours live here too, for the same reason: they were also switched on
 * the stored value inline, and a new provider would silently inherit whatever
 * the ternary's `else` branch happened to be.
 */

/** Stored values for `jobs.outbound_method`. */
export type OutboundMethod = 'eapi' | 'smtp'

/** Stored values for `jobs.inbound_method`. */
export type InboundMethod = 'eapi_webhook' | 'imap' | 'api'

const OUTBOUND_METHOD_LABELS: Readonly<Record<OutboundMethod, string>> = {
  eapi: 'eAPI',
  smtp: 'SMTP',
}

const INBOUND_METHOD_LABELS: Readonly<Record<InboundMethod, string>> = {
  eapi_webhook: 'eAPI Webhook',
  imap: 'IMAP',
  // Inbound BY API is POLLING for every provider that offers it — no provider we support
  // publishes an inbound webhook we consume under this value, and SRFax publishes none at
  // all. The unqualified label says so, so the generic (no-descriptor) fallback cannot read
  // as a push.
  api: 'API (polling)',
}

/**
 * Label for a value we have no mapping for — a provider added to the backend
 * before the UI caught up.
 *
 * Deliberately shows the STORED value (underscores opened out to spaces) rather
 * than a placeholder like "Unknown": an operator seeing `sfax_webhook` can act
 * on it, and it makes the gap self-evident instead of hiding it. Empty or absent
 * renders as the em dash used elsewhere in the UI for "no value".
 */
export function fallbackMethodLabel(value: string | null | undefined): string {
  const raw = (value ?? '').trim()
  if (!raw) return '—'
  return raw.replace(/_/g, ' ')
}

/** Display label for a stored outbound method value. Total; never throws. */
export function outboundMethodLabel(value: string | null | undefined): string {
  const raw = (value ?? '').trim()
  return OUTBOUND_METHOD_LABELS[raw as OutboundMethod] ?? fallbackMethodLabel(raw)
}

/** Display label for a stored inbound method value. Total; never throws. */
export function inboundMethodLabel(value: string | null | undefined): string {
  const raw = (value ?? '').trim()
  return INBOUND_METHOD_LABELS[raw as InboundMethod] ?? fallbackMethodLabel(raw)
}

export interface MethodOption<T extends string> {
  value: T
  label: string
}

/** Options for the outbound `<select>`, in the order they should appear. */
export const OUTBOUND_METHOD_OPTIONS: ReadonlyArray<MethodOption<OutboundMethod>> = [
  { value: 'eapi', label: OUTBOUND_METHOD_LABELS.eapi },
  { value: 'smtp', label: OUTBOUND_METHOD_LABELS.smtp },
]

/** Options for the inbound `<select>`, in the order they should appear. */
export const INBOUND_METHOD_OPTIONS: ReadonlyArray<MethodOption<InboundMethod>> = [
  { value: 'eapi_webhook', label: INBOUND_METHOD_LABELS.eapi_webhook },
  { value: 'imap', label: INBOUND_METHOD_LABELS.imap },
]

/** Default stored values for a brand-new job. */
export const DEFAULT_OUTBOUND_METHOD: OutboundMethod = 'eapi'
export const DEFAULT_INBOUND_METHOD: InboundMethod = 'eapi_webhook'

/**
 * EVERY value the column can store, which is NOT the same set as the options above.
 *
 * `*_METHOD_OPTIONS` is the provider-AGNOSTIC fallback shown before a descriptor is known, so
 * it lists only transports every provider has. The editor's descriptor-driven filter needs the
 * wider question — "can `inbound_method` hold this?" — and deriving that from the fallback list
 * is what kept `inbound_method='api'` out of the menu even once the schema accepted it. A
 * plausible operation over the wrong set: the filter was right, its set was not.
 *
 * Pinned equal to the backend `Literal`s by
 * `tests/test_provider_leg_labels_and_ui_contract.py`.
 */
export const OUTBOUND_METHOD_VALUES: ReadonlyArray<OutboundMethod> = ['eapi', 'smtp']
export const INBOUND_METHOD_VALUES: ReadonlyArray<InboundMethod> = ['eapi_webhook', 'imap', 'api']

export interface MethodBadgeStyle {
  background: string
  color: string
}

/** Neutral badge for a method we have no colour for — legible, not invisible. */
const UNKNOWN_METHOD_BADGE: MethodBadgeStyle = { background: '#333333', color: '#e0e0e0' }

const OUTBOUND_METHOD_BADGES: Readonly<Record<OutboundMethod, MethodBadgeStyle>> = {
  eapi: { background: '#1a3a52', color: '#e3f2fd' },
  smtp: { background: '#4a2233', color: '#e3f2fd' },
}

const INBOUND_METHOD_BADGES: Readonly<Record<InboundMethod, MethodBadgeStyle>> = {
  eapi_webhook: { background: '#1e3a24', color: '#e8f5e9' },
  imap: { background: '#4a3618', color: '#e8f5e9' },
  api: { background: '#123a3a', color: '#e0f7f7' },
}

export function outboundMethodBadge(value: string | null | undefined): MethodBadgeStyle {
  return OUTBOUND_METHOD_BADGES[(value ?? '').trim() as OutboundMethod] ?? UNKNOWN_METHOD_BADGE
}

export function inboundMethodBadge(value: string | null | undefined): MethodBadgeStyle {
  return INBOUND_METHOD_BADGES[(value ?? '').trim() as InboundMethod] ?? UNKNOWN_METHOD_BADGE
}

// ---------------------------------------------------------------------------------------
// G7 / G8 — provider-qualified leg labels
//
// "SMTP" outbound becomes "eFax SMTP" and "IMAP" inbound becomes "eFax IMAP", because
// both only work with eFax: the recipient is {DID}@{eFax gateway domain} from a closed
// list, correlation depends on eFax echoing our Subject back as X-J2FaxSubject, the leg
// closes on eFax's X-J2FaxResultCode / X-J2FaxDateTime headers, and the gateway body-page
// constant was measured on eFax only.
//
// THE STORED VALUES DO NOT CHANGE (D-RENAME-SCOPE). `transport` stays 'smtp' / 'imap' and
// the eFax-ness comes from the `provider` column, which is its own column on the
// measurement rows (D-3). A value migration would touch 41 comparison sites in shipped
// code to solve a problem the column already solves, so this is a relabel and nothing more
// — no migration, no filter change, no export change (G7.9 / G8.7).
//
// The label is derived from the (provider, transport) PAIR by this one function so a
// provider-qualified label is never hand-maintained per surface (G7.2). methods.ts remains
// the only place a stored value becomes visible text.
// ---------------------------------------------------------------------------------------

/**
 * What a leg with no recorded provider reads as.
 *
 * Measurement-row `*_provider` / `*_transport` columns are nullable with NO server default
 * and are NOT backfilled (`backend/models/run.py`), so every historical row has NULL. NULL
 * means NOT RECORDED and must never be read as a specific provider — there is no
 * `COALESCE(outbound_provider, 'efax')` anywhere in the backend and there is none here
 * either. Rendering a historical row as "eFax SMTP" would be inventing a measurement
 * (G9.7).
 */
export const PROVIDER_NOT_RECORDED = 'not recorded'

/** Display label for a provider slug. */
const PROVIDER_LABELS: Readonly<Record<string, string>> = {
  efax: 'eFax',
  sfax: 'SFax',
  // The brand's own capitalisation: SRFax. It is one character from `SFax`, so a truncated
  // or lowercased render makes two different providers look like one.
  srfax: 'SRFax',
}

/**
 * Transport labels PER PROVIDER, because the transport's own name is provider-specific:
 * eFax's REST API is branded "eAPI" and SFax's is plainly "API", so one shared transport
 * table would print "eFax API" or "SFax eAPI" — both wrong.
 *
 * Both spellings of each transport are accepted on purpose. The stored job method values
 * are 'eapi' / 'eapi_webhook' while the provider registry's transport names (and so the
 * `*_transport` measurement columns and the `GET /api/providers` descriptor) are 'api' /
 * 'webhook'. Accepting both means neither vocabulary has to be migrated into the other.
 */
const TRANSPORT_LABELS_BY_PROVIDER: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  efax: {
    api: 'eAPI',
    eapi: 'eAPI',
    smtp: 'SMTP',
    webhook: 'eAPI Webhook',
    eapi_webhook: 'eAPI Webhook',
    imap: 'IMAP',
  },
  sfax: {
    api: 'API',
    webhook: 'API Webhook',
    api_webhook: 'API Webhook',
    imap: 'IMAP',
  },
  // SRFax's labels say what the transport DOES, because the bare name would mislead twice:
  // its inbound API leg is POLLING (there is no inbound webhook at all), and its webhook leg
  // is OUTBOUND — a per-transmission send-status callback, the opposite direction from every
  // other webhook in this table.
  // `api` is the one transport SRFax uses in BOTH directions, and they are different
  // mechanisms — so this flat table cannot be right for both. It holds the INBOUND reading;
  // the outbound one is overridden per leg below.
  srfax: {
    api: 'API (polling)',
    webhook: 'Send Callback',
    api_webhook: 'Send Callback',
    smtp: 'Fax by Email',
    imap: 'IMAP',
  },
}

/**
 * `provider -> leg -> transport -> label`, consulted BEFORE the flat table above and holding
 * only the pairs where one transport name means two different mechanisms on the two legs.
 *
 * WHY IT EXISTS, reported by the operator: SRFax's OUTBOUND api leg read "API (polling)".
 * Sending is `Queue_Fax` — one HTTPS POST returning a `FaxDetailsID` and nothing else; the
 * polling (`Get_FaxStatus`) is how the outbound STATUS is settled afterwards, and the
 * `sNotifyURL` callback can settle it by push instead. Neither is what an operator picks when
 * they choose how to SEND. The INBOUND api leg genuinely is polling.
 *
 * NO STORED VALUE CHANGES. This is the label table, not the method vocabulary.
 *
 * Mirrors `backend/providers/labels.py::TRANSPORT_LABELS_BY_PROVIDER_LEG`;
 * `tests/test_label_parity.py` asserts the two agree.
 */
const TRANSPORT_LABELS_BY_PROVIDER_LEG: Readonly<
  Record<string, Readonly<Record<string, Readonly<Record<string, string>>>>>
> = {
  srfax: {
    outbound: {
      // Named for what it does: it queues a fax for sending.
      api: 'API (Queue_Fax)',
    },
  },
}

/** Fallback transport labels for a provider the UI has no table for yet. */
const GENERIC_TRANSPORT_LABELS: Readonly<Record<string, string>> = {
  api: 'API',
  eapi: 'eAPI',
  smtp: 'SMTP',
  webhook: 'Webhook',
  eapi_webhook: 'eAPI Webhook',
  api_webhook: 'API Webhook',
  imap: 'IMAP',
}

// ---------------------------------------------------------------------------------------
// G4.2 / T4.20 — THE TABLES ABOVE ARE NOW A BUILD-TIME FALLBACK, NOT THE AUTHORITY.
//
// `backend/providers/labels.py` is the authority and publishes its table per provider in
// `GET /api/providers` as `labels: { provider, transports, not_recorded }`. That payload is
// merged over the literals above as soon as any page fetches the registry, which is what stops
// this file and the backend drifting — the failure that had Slack printing a bare `efax` while
// this file printed "eFax SMTP".
//
// The literals STAY, deliberately. A registry fetch that fails must degrade to today's
// behaviour rather than to blank labels: a label is the only thing telling an operator which
// vendor a measurement belongs to, and an empty one is worse than a slightly stale one.
//
// The overlay is additive per provider, so a provider the backend knows and this file does not
// gains a real table, and a provider this file knows and the payload omits keeps its own.
// ---------------------------------------------------------------------------------------

/** The shape `applyProviderLabelTables` consumes. Structural, so no import from the client. */
export interface ProviderLabelSource {
  slug: string
  labels?: {
    provider?: string
    transports?: Record<string, string>
    transports_by_leg?: Record<string, Record<string, string>>
    not_recorded?: string
  } | null
}

const providerLabelOverlay: Record<string, string> = {}
const transportLabelOverlay: Record<string, Record<string, string>> = {}
const transportLegLabelOverlay: Record<string, Record<string, Record<string, string>>> = {}

/**
 * Adopt the label tables the backend published. Idempotent, and safe to call repeatedly.
 *
 * Silently ignores a provider with no `labels` block rather than clearing that provider's
 * fallback: an older backend that does not publish labels must not blank the UI.
 */
export function applyProviderLabelTables(providers: readonly ProviderLabelSource[] | null | undefined): void {
  for (const p of providers ?? []) {
    const slug = (p?.slug ?? '').trim().toLowerCase()
    if (!slug || !p?.labels) continue
    if (p.labels.provider) providerLabelOverlay[slug] = p.labels.provider
    if (p.labels.transports && Object.keys(p.labels.transports).length > 0) {
      transportLabelOverlay[slug] = { ...p.labels.transports }
    }
    if (p.labels.transports_by_leg && Object.keys(p.labels.transports_by_leg).length > 0) {
      const byLeg: Record<string, Record<string, string>> = {}
      for (const [leg, table] of Object.entries(p.labels.transports_by_leg)) {
        byLeg[leg.trim().toLowerCase()] = { ...table }
      }
      transportLegLabelOverlay[slug] = byLeg
    }
  }
}

/** Test seam: forget the fetched tables so the literals are exercised again. */
export function resetProviderLabelTables(): void {
  for (const key of Object.keys(providerLabelOverlay)) delete providerLabelOverlay[key]
  for (const key of Object.keys(transportLabelOverlay)) delete transportLabelOverlay[key]
  for (const key of Object.keys(transportLegLabelOverlay)) delete transportLegLabelOverlay[key]
}

/** Display label for a provider slug. A provider we do not know renders its own slug. */
export function providerLabel(provider: string | null | undefined): string {
  const raw = (provider ?? '').trim().toLowerCase()
  if (!raw) return PROVIDER_NOT_RECORDED
  return providerLabelOverlay[raw] ?? PROVIDER_LABELS[raw] ?? fallbackMethodLabel(raw)
}

/** Display label for one transport, qualified by the provider whose transport it is. */
export function transportLabel(
  provider: string | null | undefined,
  transport: string | null | undefined,
  leg?: string | null,
): string {
  const p = (provider ?? '').trim().toLowerCase()
  const t = (transport ?? '').trim().toLowerCase()
  if (!t) return ''
  const lg = (leg ?? '').trim().toLowerCase()
  if (lg) {
    const perLeg =
      transportLegLabelOverlay[p]?.[lg]?.[t] ?? TRANSPORT_LABELS_BY_PROVIDER_LEG[p]?.[lg]?.[t]
    if (perLeg) return perLeg
  }
  return (
    transportLabelOverlay[p]?.[t] ??
    TRANSPORT_LABELS_BY_PROVIDER[p]?.[t] ??
    GENERIC_TRANSPORT_LABELS[t] ??
    fallbackMethodLabel(t)
  )
}

/**
 * The label for one leg of one transmission, derived from the (provider, transport) pair.
 *
 * `'efax' + 'smtp'` → `"eFax SMTP"`, `'efax' + 'imap'` → `"eFax IMAP"`, `'efax' + 'api'` →
 * `"eFax eAPI"`, `'efax' + 'webhook'` → `"eFax eAPI Webhook"`, and the SFax equivalents.
 *
 * A missing provider renders `"not recorded"`, NEVER eFax. A recorded provider whose
 * transport is missing STATES the gap rather than blanking it, on the same principle as
 * the provider capabilities: a fact we do not have is different from a fact that does not
 * apply, and both beat an empty cell.
 */
export function legLabel(
  provider: string | null | undefined,
  transport: string | null | undefined,
  leg?: string | null,
): string {
  const p = (provider ?? '').trim().toLowerCase()
  if (!p) return PROVIDER_NOT_RECORDED
  const t = transportLabel(p, transport, leg)
  if (!t) return `${providerLabel(p)} (transport not recorded)`
  return `${providerLabel(p)} ${t}`
}

/**
 * The provider-registry transport name for a stored job method value.
 *
 * The job columns and the registry use different vocabularies for the same two transports
 * ('eapi' vs 'api', 'eapi_webhook' vs 'webhook'). This is the ONE place that bridge is
 * written down, so a descriptor lookup keyed on a job's stored method cannot silently miss.
 * Neither vocabulary is migrated — see the D-RENAME-SCOPE note above.
 */
const METHOD_VALUE_TO_TRANSPORT: Readonly<Record<string, string>> = {
  eapi: 'api',
  api: 'api',
  smtp: 'smtp',
  eapi_webhook: 'webhook',
  webhook: 'webhook',
  imap: 'imap',
}

export function transportForMethodValue(method: string | null | undefined): string {
  const raw = (method ?? '').trim().toLowerCase()
  return METHOD_VALUE_TO_TRANSPORT[raw] ?? raw
}

/**
 * The STORED job method value for a registry transport name — the inverse bridge.
 *
 * Needed because the editor's transport options come from the provider's declared set
 * (which speaks 'api' / 'webhook') while the value it must write to `jobs.outbound_method`
 * is the stored literal ('eapi' / 'eapi_webhook'). Going through this function is what
 * keeps the descriptor-driven form writing UNCHANGED stored values (G7.9).
 */
const TRANSPORT_TO_METHOD_VALUE: Readonly<Record<string, string>> = {
  api: 'eapi',
  smtp: 'smtp',
  webhook: 'eapi_webhook',
  imap: 'imap',
}

/**
 * The inverse bridge is NOT leg-agnostic, and treating it as one is what made SRFax's real
 * inbound leg unreachable (B30). `api` inverts to the OUTBOUND value `'eapi'` above; on the
 * INBOUND leg the stored value naming inbound-by-API is `'api'` itself. A shared table has to
 * pick one, and picking the outbound one silently mapped SRFax's declared `INBOUND_API` onto
 * `inbound_method='eapi'`, which the schema refuses — so the leg was filtered out of the menu
 * rather than offered.
 *
 * Only the entries that DIFFER from the flat table are listed; anything absent falls through,
 * so `webhook` and `imap` keep one spelling for both legs.
 */
const TRANSPORT_TO_METHOD_VALUE_BY_LEG: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  inbound: {
    api: 'api',
  },
}

export function methodValueForTransport(
  transport: string | null | undefined,
  leg?: string | null,
): string {
  const raw = (transport ?? '').trim().toLowerCase()
  const lg = (leg ?? '').trim().toLowerCase()
  if (lg) {
    const perLeg = TRANSPORT_TO_METHOD_VALUE_BY_LEG[lg]?.[raw]
    if (perLeg) return perLeg
  }
  return TRANSPORT_TO_METHOD_VALUE[raw] ?? raw
}

/**
 * One line per (provider, transport) saying what makes that method the PROVIDER's rather
 * than the operator's — the substance behind the G7/G8 rename.
 *
 * This lives in a data table rather than in the editor's JSX for the reason G9.6 gives: a
 * per-provider branch in the form is how the form stops being renderable from the registry.
 * Adding SFax adds rows here, not `{provider === 'sfax' && ...}` in a component.
 */
const LEG_SPECIFICITY_NOTES: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  efax: {
    // FINISH-LINE A2 item 5 — the clause about the gateway cover-page count (measured on eFax
    // only) and about the operator's relay host/port/mailbox being their own and unconstrained
    // was trimmed from this line. Both facts are kept in Help: the providers intro states the
    // page arithmetic was measured on eFax alone, and the `eFax SMTP` glossary entry states the
    // relay credentials are the operator's own. The counting itself is implemented in the
    // quality page-count comparison, not here. The wording above deliberately does not quote
    // the removed sentence: the guard reads this file's text, and a quoted caveat in a comment
    // is how a "removed" string keeps reading as present.
    smtp:
      'eFax-specific: the fax is addressed to {DID}@{eFax gateway domain} from eFax’s closed ' +
      'domain list, correlation depends on eFax echoing our Subject back as X-J2FaxSubject, ' +
      'and the leg closes on eFax’s X-J2FaxResultCode / X-J2FaxDateTime headers.',
    api:
      'eFax-specific: the endpoint is one of eFax’s regional eAPI hosts from a closed list, ' +
      'and the outbound leg closes on eFax’s own transmission_status — the App ID, Key, User ' +
      'ID and From DID are your eFax account’s.',
    webhook:
      'eFax-specific: eFax POSTs to a per-job URL and we verify it with HMAC-SHA256 over the ' +
      'raw body against the X-HMAC-Signature header — the HMAC secret is the one you gave ' +
      'eFax in its Notify Configuration.',
    imap:
      'eFax-specific: the message-classification grammar is eFax’s — a delivered fax is ' +
      'recognised by X-J2-Message-Type: FAX or an eFax subject, and a send notification by ' +
      'X-J2FaxResultCode — while the mail host, port and mailbox credentials below are your ' +
      'own.',
  },
  sfax: {
    api:
      'SFax-specific: a single API host, and the request is authenticated by an AES-256-CBC ' +
      'token minted from your four static SFax secrets — there is no token endpoint.',
    webhook:
      'SFax-specific: callbacks are HTTP GET and are configured per SFax ACCOUNT in the SFax ' +
      'portal, not per job, and are discriminated by faxtype rather than by URL.',
    imap:
      'SFax-specific: inbound fax by email is not documented in SFax’s API, so this grammar ' +
      'is provisional.',
  },
  srfax: {
    // FINISH-LINE A2 item 7 — the SRFax API paragraph was removed from this form. Its facts are
    // all still TRUE and all still stated in Help's "Providers and leg labels" section, which is
    // outside the admin gate:
    //   * one action-dispatched endpoint, and the account number + password travel on EVERY
    //     request with no token — so rotating the SRFax portal password breaks the integration
    //     (Help: `srfax`);
    //   * success is read from the response BODY, because SRFax reports a refusal with HTTP 200
    //     (Help: `srfax`; implemented in providers/srfax/api.py, which judges Status not
    //     response.status_code);
    //   * inbound on this transport is POLLING with no inbound webhook, and it cannot report
    //     which of several DIDs a fax arrived on (Help: `SRFax API (polling)`).
    // Omitting the key renders nothing rather than an empty box — see `legSpecificityNote`.
    webhook:
      'SRFax-specific: this is the OUTBOUND send-status callback, and its URL is supplied on ' +
      'each send — so it names the individual transmission rather than a job or an account. ' +
      'SRFax signs nothing, so the URL itself carries a per-transmission secret and is the ' +
      'only authentication there is. There is no inbound callback.',
    smtp:
      'SRFax-specific: the fax is addressed to {11-digit number}@srfax.com from an address on ' +
      'your account’s authorised-sender list (SPF mandatory), and cover-page parameters are ' +
      '“/”-separated Key=Value commands in the SUBJECT line. We never emit Pass=, which would ' +
      'put your account password in cleartext in a header every relay logs.',
    imap:
      'SRFax-specific: SRFax’s email grammar is not documented at all — no From address, no ' +
      'subject format, no attachment convention — so this leg CLASSIFIES NOTHING. Every ' +
      'message is captured verbatim for later analysis and none is consumed.',
  },
}

/**
 * The one-line statement of what makes a method the provider's own. Empty string when we
 * have nothing to say, so a caller renders nothing rather than an empty box.
 */
export function legSpecificityNote(
  provider: string | null | undefined,
  transport: string | null | undefined,
): string {
  const p = (provider ?? '').trim().toLowerCase()
  const t = (transport ?? '').trim().toLowerCase()
  return LEG_SPECIFICITY_NOTES[p]?.[t] ?? ''
}

/**
 * Label for a job's configured outbound leg, qualified by the job's outbound provider.
 *
 * `jobs.outbound_provider` is CONFIGURATION and carries a server default, so unlike a
 * measurement row it is not normally NULL — but it is still not defaulted to eFax here. A
 * job with no recorded provider falls back to the bare transport label rather than
 * claiming a brand.
 *
 * THE LEG IS NAMED, because these two functions are the only callers that can never be
 * wrong about it: `jobOutboundLabel` is the outbound leg by definition. Omitting it sent
 * every job label through the leg-agnostic table, where SRFax's `api` holds the INBOUND
 * reading — so an SRFax job configured to SEND by API read "SRFax API (polling)", naming
 * the mechanism that settles the status rather than the one that transmits the fax. Same
 * class of defect as the run row that read "eAPI" for an SRFax transmission, one level up:
 * the per-leg table existed and the call site did not ask it.
 */
export function jobOutboundLabel(
  provider: string | null | undefined,
  method: string | null | undefined,
): string {
  const p = (provider ?? '').trim().toLowerCase()
  if (!p) return outboundMethodLabel(method)
  return legLabel(p, transportForMethodValue(method), 'outbound')
}

/** As `jobOutboundLabel`, for the inbound leg. */
export function jobInboundLabel(
  provider: string | null | undefined,
  method: string | null | undefined,
): string {
  const p = (provider ?? '').trim().toLowerCase()
  if (!p) return inboundMethodLabel(method)
  return legLabel(p, transportForMethodValue(method), 'inbound')
}

/**
 * Label for a key in the `POST /api/jobs/{id}/test-connection` result map.
 *
 * That endpoint keys each check by the leg it tested, and its keys are NOT the
 * stored method values — the eAPI check is still keyed 'api'. JobDetail used to
 * render `key.toUpperCase()`, which would print `EAPI` the moment that key is
 * renamed, so the mapping is explicit and accepts both spellings.
 *
 * G7.3 / G8.3: the label is now provider-qualified in step with every other surface, so a
 * connection check on an eFax job reads "eFax SMTP" rather than the old bare "SMTP". The
 * dual spellings survive for the reason they were added — the result keys are not the
 * stored method values — and both spellings are qualified, so renaming a result key cannot
 * quietly drop the provider back off.
 *
 * `provider` is OPTIONAL and defaults to unqualified. It is never defaulted to eFax: a
 * caller that cannot say which provider it tested gets the bare label, not an invented one.
 * Anything unrecognised (e.g. 'warning') keeps the previous uppercase treatment.
 */
const CONNECTION_CHECK_LABELS: Readonly<Record<string, string>> = {
  api: 'eAPI',
  eapi: 'eAPI',
  smtp: 'SMTP',
  imap: 'IMAP',
  webhook: 'eAPI Webhook',
  eapi_webhook: 'eAPI Webhook',
}

export function connectionCheckLabel(key: string, provider?: string | null): string {
  const raw = (key ?? '').trim().toLowerCase()
  const p = (provider ?? '').trim().toLowerCase()
  if (p) {
    const qualified = TRANSPORT_LABELS_BY_PROVIDER[p]?.[raw] ?? GENERIC_TRANSPORT_LABELS[raw]
    if (qualified) return `${providerLabel(p)} ${qualified}`
  }
  return CONNECTION_CHECK_LABELS[raw] ?? (key ?? '').toUpperCase()
}
